import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Modal, Form, Table, Badge, Alert, Spinner, Tabs, Tab, ProgressBar } from 'react-bootstrap';
import { FaUserPlus, FaTrash, FaEdit, FaUsers, FaChartBar, FaEye, FaRedo, FaClock, FaMousePointer, FaEnvelope } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import adminService from '../services/adminService';

const UserManagement = () => {
  const { user, profile } = useAuth();
  const [users, setUsers] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditRoleModal, setShowEditRoleModal] = useState(false);
  const [showUserActivityModal, setShowUserActivityModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userActivity, setUserActivity] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    fullName: '',
    email: '',
    password: '',
    isAdmin: false
  });

  // Check if current user is super admin
  const isSuperAdmin = profile?.is_super_admin;

  useEffect(() => {
    if (isSuperAdmin) {
      fetchUsers();
      fetchStatistics();
    } else {
      setLoading(false);
      setStatsLoading(false);
    }
  }, [isSuperAdmin]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await adminService.getUsers();
      setUsers(data);
    } catch (error) {
      toast.error('Failed to fetch users');
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      setStatsLoading(true);
      const data = await adminService.getUserStatistics();
      setStatistics(data);
    } catch (error) {
      toast.error('Failed to fetch user statistics');
      console.error('Error fetching statistics:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchUserActivity = async (userId) => {
    try {
      const data = await adminService.getUserActivity(userId);
      setUserActivity(data);
      setShowUserActivityModal(true);
    } catch (error) {
      toast.error('Failed to fetch user activity');
      console.error('Error fetching user activity:', error);
    }
  };

  const handleResetUserStats = async (userId, username) => {
    if (window.confirm(`Are you sure you want to reset statistics for user "${username}"?`)) {
      try {
        await adminService.resetUserStats(userId);
        toast.success('User statistics reset successfully');
        fetchUsers();
        fetchStatistics();
      } catch (error) {
        toast.error(error.message || 'Failed to reset user statistics');
      }
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!formData.username || !formData.email || !formData.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      const result = await adminService.createUser(
        formData.email, 
        formData.username,
        formData.fullName || formData.username, 
        formData.password,
        formData.isAdmin
      );
      
      // Show success with credentials
      toast.success(
        <div>
          <strong>✅ User Created Successfully!</strong><br/>
          <small>Full Name: {result.user.fullName}</small><br/>
          <small>Email: {result.user.email}</small><br/>
          <small>Username: {result.user.username}</small><br/>
          <small>Temporary Password: <code>{result.user.temporaryPassword}</code></small><br/><br/>
          <small style={{color: '#856404'}}>⚠️ Share these credentials with the user securely!</small>
        </div>,
        { autoClose: 15000 }
      );
      
      setShowAddUserModal(false);
      setFormData({
        username: '',
        fullName: '',
        email: '',
        password: '',
        isAdmin: false
      });
      
      // Refresh users list
      fetchUsers();
    } catch (error) {
      toast.error(error.message || 'Failed to create user');
    }
  };

  const handleDeleteUser = async (userId, username) => {
    const confirmMessage = `⚠️ PERMANENT DELETION WARNING ⚠️

Are you absolutely sure you want to delete user "${username}"?

This will PERMANENTLY delete:
• User account and profile
• All projects created by this user
• All files uploaded by this user
• All folders created by this user
• All clients created by this user
• All invitations sent by this user

This action CANNOT be undone!

Type "DELETE" to confirm:`;

    const confirmation = window.prompt(confirmMessage);
    
    if (confirmation === 'DELETE') {
      try {
        const result = await adminService.deleteUser(userId);
        
        // Show detailed success message
        toast.success(
          <div>
            <strong>User "{result.deletedUser}" deleted successfully!</strong><br/>
            <small>
              Deleted: {result.deletedData.files} files, {result.deletedData.folders} folders, 
              {result.deletedData.projects} projects, {result.deletedData.clients} clients
            </small>
          </div>,
          { autoClose: 8000 }
        );
        
        fetchUsers();
        fetchStatistics();
      } catch (error) {
        toast.error(
          <div>
            <strong>Failed to delete user</strong><br/>
            <small>{error.message}</small>
          </div>
        );
      }
    } else if (confirmation !== null) {
      toast.warning('Deletion cancelled - you must type "DELETE" exactly to confirm');
    }
  };

  const handleEditRole = (user) => {
    setSelectedUser(user);
    setShowEditRoleModal(true);
  };

  const handleUpdateRole = async (e) => {
    e.preventDefault();
    
    if (!selectedUser) return;

    try {
      await adminService.updateUserRole(selectedUser.id, selectedUser.isAdmin);
      toast.success('User role updated successfully');
      setShowEditRoleModal(false);
      setSelectedUser(null);
      fetchUsers();
      fetchStatistics();
    } catch (error) {
      toast.error(error.message || 'Failed to update user role');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleRoleChange = (e) => {
    const { checked } = e.target;
    setSelectedUser(prev => ({
      ...prev,
      isAdmin: checked
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const getActivityLevel = (clickCount) => {
    if (clickCount === 0) return { level: 'No Activity', variant: 'secondary', percentage: 0 };
    if (clickCount < 50) return { level: 'Low', variant: 'info', percentage: 25 };
    if (clickCount < 200) return { level: 'Medium', variant: 'warning', percentage: 50 };
    if (clickCount < 500) return { level: 'High', variant: 'success', percentage: 75 };
    return { level: 'Very High', variant: 'danger', percentage: 100 };
  };

  if (!isSuperAdmin) {
    return (
      <Container>
        <Alert variant="danger" className="text-center">
          <h4>Access Denied</h4>
          <p>You don't have permission to access user management. Only super administrators can manage users.</p>
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container>
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2><FaUsers className="me-2" />User Management</h2>
          <p className="text-muted">Manage system users and their permissions</p>
        </div>
        <div>
          <Button 
            variant="primary" 
            onClick={() => setShowAddUserModal(true)}
          >
            <FaUserPlus className="me-1" /> Create User
          </Button>
        </div>
      </div>

      <Tabs defaultActiveKey="users" className="mb-4">
        <Tab eventKey="users" title={<><FaUsers className="me-1" />Users ({users.length})</>}>
          <Card>
            <Card.Body>
              {users.length === 0 ? (
                <Alert variant="info" className="text-center">
                  No users found. Add your first user to get started.
                </Alert>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Full Name</th>
                      <th>Username</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Last Login</th>
                      <th>Activity</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((userItem, index) => {
                      const activity = getActivityLevel(userItem.clickCount || 0);
                      const canDelete = (!userItem.isSuperAdmin || user?.email === 'randokraav@gmail.com') && userItem.id !== user?.id;
                      console.log(`🔍 User item ${index}:`, {
                        username: userItem.username,
                        isAdmin: userItem.isAdmin,
                        isSuperAdmin: userItem.isSuperAdmin,
                        currentUserEmail: user?.email,
                        isOwnAccount: userItem.id === user?.id,
                        canDelete: canDelete
                      });
                      return (
                        <tr key={`user-${userItem.id}-${index}`}>
                          <td>
                            <strong>{userItem.fullName || userItem.username}</strong>
                            {userItem.id === user.id && (
                              <Badge bg="info" className="ms-2">You</Badge>
                            )}
                          </td>
                          <td>{userItem.username}</td>
                          <td>{userItem.email}</td>
                          <td>
                            {userItem.isSuperAdmin ? (
                              <Badge bg="danger">Super Admin</Badge>
                            ) : userItem.isAdmin ? (
                              <Badge bg="warning">Admin</Badge>
                            ) : (
                              <Badge bg="secondary">User</Badge>
                            )}
                          </td>
                          <td>
                            <small>{formatDate(userItem.lastLogin)}</small>
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <ProgressBar 
                                variant={activity.variant} 
                                now={activity.percentage} 
                                style={{ width: '60px', height: '8px' }}
                                className="me-2"
                              />
                              <small>{userItem.clickCount || 0} clicks</small>
                            </div>
                          </td>
                          <td>
                            <Button
                              variant="outline-info"
                              size="sm"
                              className="me-1"
                              onClick={() => fetchUserActivity(userItem.id)}
                            >
                              <FaEye />
                            </Button>
                            {(!userItem.isSuperAdmin || user?.email === 'randokraav@gmail.com') && userItem.id !== user?.id && (
                              <>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="me-1"
                                  onClick={() => handleEditRole(userItem)}
                                  title="Edit Role"
                                >
                                  <FaEdit />
                                </Button>
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  className="me-1"
                                  onClick={() => handleResetUserStats(userItem.id, userItem.username)}
                                  title="Reset Statistics"
                                >
                                  <FaRedo />
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDeleteUser(userItem.id, userItem.username)}
                                  title="Delete User Permanently"
                                >
                                  <FaTrash />
                                </Button>
                              </>
                            )}
                            {userItem.isSuperAdmin && user?.email !== 'randokraav@gmail.com' && (
                              <span className="text-muted small">
                                <Badge bg="light" text="dark">Protected</Badge>
                                <small className="ms-1">Super admins cannot be deleted</small>
                              </span>
                            )}
                            {userItem.id === user?.id && (
                              <span className="text-muted small">
                                <Badge bg="info" text="dark">Your Account</Badge>
                                <small className="ms-1">Cannot delete yourself</small>
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="statistics" title={<><FaChartBar className="me-1" />Statistics</>}>
          {statsLoading ? (
            <div className="text-center my-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading statistics...</span>
              </Spinner>
            </div>
          ) : statistics ? (
            <>
              <Row className="mb-4">
                <Col md={3}>
                  <Card className="text-center">
                    <Card.Body>
                      <h3 className="text-primary">{statistics.overview.totalUsers}</h3>
                      <p className="mb-0">Total Users</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center">
                    <Card.Body>
                      <h3 className="text-success">{statistics.overview.activeUsers}</h3>
                      <p className="mb-0">Active (30 days)</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center">
                    <Card.Body>
                      <h3 className="text-warning">{statistics.overview.adminUsers}</h3>
                      <p className="mb-0">Admins</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center">
                    <Card.Body>
                      <h3 className="text-danger">{statistics.overview.superAdminUsers}</h3>
                      <p className="mb-0">Super Admins</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Card>
                    <Card.Header>
                      <h5><FaMousePointer className="me-2" />Most Active Users</h5>
                    </Card.Header>
                    <Card.Body>
                      <Table size="sm">
                        <thead>
                          <tr>
                            <th>User</th>
                            <th>Clicks</th>
                            <th>Logins</th>
                          </tr>
                        </thead>
                        <tbody>
                          {statistics.mostActiveUsers.map(user => (
                            <tr key={user.id}>
                              <td>{user.username}</td>
                              <td>{user.clickCount}</td>
                              <td>{user.loginCount}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card>
                    <Card.Header>
                      <h5><FaClock className="me-2" />Recent Logins</h5>
                    </Card.Header>
                    <Card.Body>
                      <Table size="sm">
                        <thead>
                          <tr>
                            <th>User</th>
                            <th>Last Login</th>
                            <th>Total Logins</th>
                          </tr>
                        </thead>
                        <tbody>
                          {statistics.recentLogins.map(user => (
                            <tr key={user.id}>
                              <td>{user.username}</td>
                              <td><small>{formatDate(user.lastLogin)}</small></td>
                              <td>{user.loginCount}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          ) : (
            <Alert variant="warning">Failed to load statistics</Alert>
          )}
        </Tab>
      </Tabs>

      {/* Create User Modal */}
      <Modal show={showAddUserModal} onHide={() => setShowAddUserModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Create New User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <strong>Direct User Creation:</strong> Create a user account with a temporary password. Share the credentials securely with the user.
          </Alert>
          <Form onSubmit={handleCreateUser}>
            <Form.Group className="mb-3">
              <Form.Label>Full Name *</Form.Label>
              <Form.Control
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                placeholder="Enter full name (e.g., John Smith)"
                required
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Username *</Form.Label>
                  <Form.Control
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    placeholder="Enter username"
                    required
                  />
                  <Form.Text className="text-muted">
                    Used for login display
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email *</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter email"
                    required
                  />
                  <Form.Text className="text-muted">
                    Used for login
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label>Temporary Password *</Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter temporary password (min 6 characters)"
                required
                minLength={6}
              />
              <Form.Text className="text-muted">
                User can change this password after first login
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="isAdmin"
                checked={formData.isAdmin}
                onChange={handleInputChange}
                label="Grant admin privileges"
              />
            </Form.Group>

            <div className="d-flex justify-content-end">
              <Button 
                variant="secondary" 
                onClick={() => setShowAddUserModal(false)}
                className="me-2"
              >
                Cancel
              </Button>
              <Button variant="success" type="submit">
                Create User
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Role Modal */}
      <Modal show={showEditRoleModal} onHide={() => setShowEditRoleModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit User Role</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <Form onSubmit={handleUpdateRole}>
              <Alert variant="info">
                <strong>User:</strong> {selectedUser.username} ({selectedUser.email})
              </Alert>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  checked={selectedUser.isAdmin}
                  onChange={handleRoleChange}
                  label="Grant admin privileges"
                />
                <Form.Text className="text-muted">
                  Admins have elevated permissions within the system.
                </Form.Text>
              </Form.Group>

              <div className="d-flex justify-content-end">
                <Button 
                  variant="secondary" 
                  onClick={() => setShowEditRoleModal(false)}
                  className="me-2"
                >
                  Cancel
                </Button>
                <Button variant="primary" type="submit">
                  Update Role
                </Button>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>

      {/* User Activity Modal */}
      <Modal show={showUserActivityModal} onHide={() => setShowUserActivityModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>User Activity Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {userActivity && (
            <>
              <Row className="mb-4">
                <Col md={6}>
                  <Card>
                    <Card.Body>
                      <h6>User Information</h6>
                      <p><strong>Username:</strong> {userActivity.username}</p>
                      <p><strong>Email:</strong> {userActivity.email}</p>
                      <p><strong>Role:</strong> {
                        userActivity.isSuperAdmin ? 'Super Admin' :
                        userActivity.isAdmin ? 'Admin' : 'User'
                      }</p>
                      <p><strong>Member Since:</strong> {formatDate(userActivity.createdAt)}</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card>
                    <Card.Body>
                      <h6>Activity Statistics</h6>
                      <p><strong>Total Clicks:</strong> {userActivity.clickCount || 0}</p>
                      <p><strong>Total Logins:</strong> {userActivity.loginCount || 0}</p>
                      <p><strong>Last Login:</strong> {formatDate(userActivity.lastLogin)}</p>
                      <p><strong>Last Updated:</strong> {formatDate(userActivity.updatedAt)}</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              
              <Card>
                <Card.Header>
                  <h6>Activity Level</h6>
                </Card.Header>
                <Card.Body>
                  {(() => {
                    const activity = getActivityLevel(userActivity.clickCount || 0);
                    return (
                      <div>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span>Activity Level: <Badge bg={activity.variant}>{activity.level}</Badge></span>
                          <span>{userActivity.clickCount || 0} total clicks</span>
                        </div>
                        <ProgressBar 
                          variant={activity.variant} 
                          now={activity.percentage} 
                          label={`${activity.percentage}%`}
                        />
                      </div>
                    );
                  })()}
                </Card.Body>
              </Card>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUserActivityModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default UserManagement;
